import { useOpportunityStatuses } from '@opportunities/-hooks/useOpportunityStatuses'
import { useMemo } from 'react'
import { OPPORTUNITY_STATUS_ID } from 'shared'
import type { OpportunityStatusState } from '../-components/OpportunityStatusSelect'
import { useCloseReasons } from './useCloseReasons'
import { useOpportunityStatusFormState } from './useOpportunityStatusFormState'
import { useOpportunityStatusValidation } from './useOpportunityStatusValidation'

export function useOpportunityStatusForm(initialState: OpportunityStatusState) {
  // データ取得
  const { data: statusesData, isLoading: isLoadingStatuses } = useOpportunityStatuses()
  const { data: closeReasonsData, isLoading: isLoadingCloseReasons } = useCloseReasons()

  // 派生値
  const statuses = statusesData?.statuses ?? []

  // 状態管理
  const {
    statusState,
    touched,
    handleStatusChange,
    handleCloseReasonChange,
    handleAdditionalCloseReasonChange,
    handleGCompanyNameChange,
    handleGCompanyContactChange,
    touchAllStatusFields,
  } = useOpportunityStatusFormState(initialState, statuses)

  const currentStatus = useMemo(() => {
    return statuses.find((s) => s.id === statusState.statusId)
  }, [statuses, statusState.statusId])

  const isClosed = currentStatus?.is_completed || false
  const isSystemManaged = currentStatus?.is_system_managed ?? false
  const isGCompanyFlow = statusState.statusId === OPPORTUNITY_STATUS_ID.G_COMPANY_STATUS_ID

  const isAdditionalCloseReasonRequired = useMemo(() => {
    if (!statusState.closeReasonId || !closeReasonsData) return false
    const reason = closeReasonsData.closeReasons.find((r) => r.id === statusState.closeReasonId)
    return reason?.isAdditionalCloseReasonRequired || false
  }, [statusState.closeReasonId, closeReasonsData])

  const groupedCloseReasons = useMemo(() => {
    if (!closeReasonsData) return {}
    const grouped: Record<string, typeof closeReasonsData.closeReasons> = {}
    for (const reason of closeReasonsData.closeReasons) {
      const category = reason.categoryName
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category]?.push(reason)
    }
    return grouped
  }, [closeReasonsData])

  // バリデーション
  const { statusErrors, hasAllStatusErrors } = useOpportunityStatusValidation({
    statusState,
    touched,
    isClosed,
    isGCompanyFlow,
    isAdditionalCloseReasonRequired,
  })

  return {
    // 状態
    statusState,
    // ハンドラ
    handleStatusChange,
    handleCloseReasonChange,
    handleAdditionalCloseReasonChange,
    handleGCompanyNameChange,
    handleGCompanyContactChange,
    touchAllStatusFields,
    // バリデーション
    statusErrors,
    hasAllStatusErrors,
    // 派生データ
    statuses,
    isSystemManaged,
    groupedCloseReasons,
    isStatusDataLoading: isLoadingStatuses || isLoadingCloseReasons,
    currentStatus,
    isClosed,
    isGCompanyFlow,
    isAdditionalCloseReasonRequired,
  }
}
